import { useData, useSession } from '@yingbaba/api';
import { PageTitle, QueryState, ActionDialog, Notice, Status, Empty } from '@yingbaba/ui';
import { datetime } from '@yingbaba/utils';
import type { RetentionPolicy, DeletionRequest, BusinessTimeoutPolicy } from '@yingbaba/types';

export function LifecycleManagement() {
  const q = useData<DeletionRequest[]>('/admin/retention/requests'),
    policy = useData<RetentionPolicy>('/admin/retention/policy');
  const session = useSession(),
    admin = session.data?.account.platform_role === 'super_admin';
  return (
    <>
      <PageTitle
        title="注销与数据留存"
        description="按申请快照分阶段处理，未结交易与争议保全会阻止删除。"
      />
      <div className="stack">
        <section className="panel stack">
          <QueryState query={policy}>
            {policy.data && (
              <>
                <p>
                  账号冷静期 {policy.data.account_cooling_days} 天 · 组织删除等待期{' '}
                  {policy.data.public_content_days} 天 · 非交易私有资料{' '}
                  {policy.data.business_retention_days
                    ? `注销完成后留存 ${policy.data.business_retention_days} 天`
                    : '限制访问，待确定留存期限'}{' '}
                  · 政策 V{policy.data.version}
                </p>
                <Notice>
                  期限默认仅影响新申请；既有受限档案可逐条明确应用当前政策。共享项目证据、合同、账本与审计历史保留。保全期间不执行资料删除；备份中的数据按备份留存政策淘汰。
                </Notice>
                {admin && (
                  <ActionDialog
                    title="修改留存政策"
                    trigger="配置期限"
                    method="PUT"
                    path="/admin/retention/policy"
                    submitLabel="保存政策"
                    fields={[
                      {
                        name: 'account_cooling_days',
                        label: '账号冷静期（天）',
                        type: 'number',
                        min: 1,
                        max: 30,
                        value: String(policy.data.account_cooling_days),
                      },
                      {
                        name: 'public_content_days',
                        label: '组织删除等待期（天）',
                        type: 'number',
                        min: 1,
                        max: 365,
                        value: String(policy.data.public_content_days),
                      },
                      {
                        name: 'business_retention_days',
                        label: '非交易私有资料留存（天，0 为暂不自动删除）',
                        type: 'number',
                        min: 0,
                        max: 36500,
                        value: String(policy.data.business_retention_days),
                      },
                      { name: 'reason', label: '调整依据', type: 'textarea', min: 4, max: 500 },
                    ]}
                    transform={(v) => ({
                      ...v,
                      account_cooling_days: Number(v.account_cooling_days),
                      public_content_days: Number(v.public_content_days),
                      business_retention_days: Number(v.business_retention_days),
                    })}
                  />
                )}
              </>
            )}
          </QueryState>
        </section>
        <section className="panel stack">
          <h3>处理队列</h3>
          <QueryState query={q}>
            {!q.data?.length && <Empty title="暂无注销或组织删除申请" />}
            {q.data?.map((row) => (
              <div className="list-item" key={row.id}>
                <div className="grow stack" style={{ gap: 8 }}>
                  <strong>
                    {row.target_type === 'account' ? '账号' : '组织'} · {row.target_id}
                  </strong>
                  <p>
                    <Status value={row.status} /> {row.legal_hold && ' · 资料保全中'} · V
                    {row.policy_version} · 处理时间 {datetime(row.due_at)}
                  </p>
                  <p className="small">申请说明：{row.reason}</p>
                  {row.block_reason && <Notice>{row.block_reason}</Notice>}
                  {row.hold_reason && <p className="small muted">保全说明：{row.hold_reason}</p>}
                  {row.content_purged_at && (
                    <p className="small muted">
                      公开与个人资料已处理 {datetime(row.content_purged_at)}；非交易档案留存至{' '}
                      {row.business_retain_until ? datetime(row.business_retain_until) : '待配置'}
                    </p>
                  )}
                </div>
                {admin && !['withdrawn', 'completed'].includes(row.status) && (
                  <div className="row wrap">
                    {row.status === 'retained' &&
                      !row.legal_hold &&
                      !!policy.data?.business_retention_days && (
                        <ActionDialog
                          title="应用当前非交易资料留存政策"
                          trigger="应用当前政策"
                          path={`/admin/retention/requests/${row.id}/apply-policy`}
                          submitLabel="确认应用当前政策"
                          description="期限从注销实际完成时间计算，可能已到期。共享项目、合同及账本继续保留；请核对处理依据。"
                          fields={[
                            {
                              name: 'reason',
                              label: '应用依据',
                              type: 'textarea',
                              min: 5,
                              max: 500,
                            },
                          ]}
                        />
                      )}
                    <ActionDialog
                      title={row.legal_hold ? '解除资料保全' : '设置资料保全'}
                      trigger={row.legal_hold ? '解除保全' : '设置保全'}
                      path={`/admin/retention/requests/${row.id}/hold`}
                      submitLabel={row.legal_hold ? '确认解除保全' : '确认设置保全'}
                      description="保全保护资料；解除后将在满足等待期与未结义务检查后继续处理。"
                      fields={[
                        { name: 'reason', label: '处理依据', type: 'textarea', min: 4, max: 500 },
                      ]}
                      transform={(v) => ({ ...v, hold: !row.legal_hold })}
                    />
                    {!row.legal_hold &&
                      row.status === 'retained' &&
                      row.business_retain_until &&
                      new Date(row.business_retain_until) <= new Date() && (
                        <ActionDialog
                          title="清理留存到期的非交易资料"
                          trigger="到期清理"
                          path={`/admin/retention/requests/${row.id}/purge`}
                          submitLabel="确认清理非交易资料"
                          intent="destructive"
                          description="清理组织认证证件与未关联项目的 AI 草稿。共享项目、合同、财务与审计记录继续保留。"
                          fields={[
                            {
                              name: 'reason',
                              label: '删除依据',
                              type: 'textarea',
                              min: 5,
                              max: 500,
                            },
                          ]}
                        />
                      )}
                  </div>
                )}
              </div>
            ))}
          </QueryState>
        </section>
      </div>
    </>
  );
}
export function BusinessTimeoutSettings() {
  const q = useData<BusinessTimeoutPolicy>('/admin/business-timeouts/policy'),
    session = useSession();
  return (
    <>
      <PageTitle
        title="业务处理时限"
        description="超时创建平台协助事项并通知双方，仍由双方或授权人员确认处理结果。"
      />
      <section className="panel stack">
        <QueryState query={q}>
          {q.data && (
            <>
              <p>
                取消、独立退款、资源补充协议和交付验收：{q.data.response_hours}{' '}
                小时；同意后的退款财务处理：{q.data.finance_hours} 小时。
              </p>
              <Notice>
                新申请使用最新时限，已提交申请保持原期限。超时不会自动同意费用、退款或验收。相同阶段只升级一次。
              </Notice>
              {session.data?.account.platform_role === 'super_admin' && (
                <ActionDialog
                  title="配置业务处理时限"
                  trigger="调整时限"
                  method="PUT"
                  path="/admin/business-timeouts/policy"
                  submitLabel="保存处理时限"
                  fields={[
                    {
                      name: 'response_hours',
                      label: '双方响应（小时）',
                      type: 'number',
                      min: 1,
                      max: 720,
                      value: String(q.data.response_hours),
                    },
                    {
                      name: 'finance_hours',
                      label: '退款财务处理（小时）',
                      type: 'number',
                      min: 1,
                      max: 2160,
                      value: String(q.data.finance_hours),
                    },
                    { name: 'reason', label: '调整说明', type: 'textarea', min: 4, max: 500 },
                  ]}
                  transform={(v) => ({
                    ...v,
                    response_hours: Number(v.response_hours),
                    finance_hours: Number(v.finance_hours),
                  })}
                />
              )}
            </>
          )}
        </QueryState>
      </section>
    </>
  );
}
